<template>
  <modal name="request-detail-modal" :height="'auto'" :width="'650'" :adaptive="true" :scrollable="true">
    <div class="flex items-center justify-between p-4 border-b">
      <h4 class="text-base font-semibold">Talep Detayı</h4>
      <button @click="$modal.hide('request-detail-modal')"><i class="fas fa-times"></i></button>
    </div>

    <div v-if="item" class="p-4 space-y-4">
      <!-- Üst Özet -->
      <div class="grid grid-cols-2 gap-3 text-xs">
        <div class="flex justify-between"><span class="text-gray-500">Plaka</span><span class="font-medium">{{ item.vehiclePlate }}</span></div>
        <div class="flex justify-between"><span class="text-gray-500">Sürücü</span><span class="font-medium">{{ item.driverName }}</span></div>
        <div class="flex justify-between"><span class="text-gray-500">İstasyon</span><span class="font-medium">{{ item.stationName || '—' }}</span></div>
        <div class="flex justify-between"><span class="text-gray-500">Durum</span><span class="font-medium">{{ item.statusText }}</span></div>
        <div class="flex justify-between"><span class="text-gray-500">Toplam Tutar</span><span class="font-medium">{{ item.totalAmount != null ? formatMoney(item.totalAmount) + ' TL' : '—' }}</span></div>
      </div>

      <!-- Sürücü Notu -->
      <div v-if="item.requestNote" class="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
        <div class="font-medium text-blue-800 mb-1"><i class="fas fa-comment-dots mr-1"></i>Sürücü Notu</div>
        <div class="text-gray-700 italic">{{ item.requestNote }}</div>
      </div>

      <!-- CAN-bus Yakıt Seviyesi -->
      <div v-if="item.canbusFuelLevelBefore != null || item.canbusFuelLevelAfter != null" class="p-2 bg-gray-50 border border-gray-200 rounded">
        <div class="text-xs font-medium text-gray-700 mb-2"><i class="fas fa-gas-pump mr-1"></i>CAN-bus Yakıt Seviyesi</div>
        <div class="grid grid-cols-3 gap-2 text-xs">
          <div class="text-center">
            <div class="text-xxs text-gray-500">Önceki</div>
            <div class="font-semibold">{{ item.canbusFuelLevelBefore != null ? formatPercent(item.canbusFuelLevelBefore) : '—' }}</div>
          </div>
          <div class="text-center">
            <div class="text-xxs text-gray-500">Sonraki</div>
            <div class="font-semibold">{{ item.canbusFuelLevelAfter != null ? formatPercent(item.canbusFuelLevelAfter) : '—' }}</div>
          </div>
          <div class="text-center">
            <div class="text-xxs text-gray-500">Değişim</div>
            <div class="font-semibold" :class="canbusChangeClass">{{ canbusChange }}</div>
          </div>
        </div>
      </div>

      <!-- Kalemler -->
      <div>
        <div class="text-xs font-semibold text-gray-700 mb-2">Yakıt Kalemleri</div>
        <div v-if="(item.items || []).length === 0" class="text-xs text-gray-400">Bu talepte kalem bilgisi yok.</div>
        <div v-else class="space-y-2">
          <div v-for="(it, idx) in item.items" :key="idx" class="border rounded p-2 bg-gray-50">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-semibold">{{ it.fuelType }}</span>
              <span :class="itemStatusBadge(it.itemStatus).cls" class="px-1.5 py-0.5 text-xxs rounded">
                {{ itemStatusBadge(it.itemStatus).label }}
              </span>
            </div>

            <div class="grid grid-cols-2 gap-2 text-xxs">
              <div>
                <span class="text-gray-500">Talep Edilen:</span>
                <span class="font-medium">{{ requestedDisplay(it) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Onaylanan:</span>
                <span class="font-medium">{{ approvedDisplay(it) }}</span>
              </div>
              <div><span class="text-gray-500">Alınan:</span> <span class="font-medium">{{ it.actualLiters != null ? it.actualLiters + ' Lt' : '—' }}</span></div>
              <div><span class="text-gray-500">Tutar:</span> <span class="font-medium">{{ it.totalAmount != null ? formatMoney(it.totalAmount) + ' TL' : '—' }}</span></div>
            </div>

            <div v-if="it.rejectionReason" class="mt-1 p-1 bg-red-50 text-red-700 text-xxs rounded">
              <i class="fas fa-info-circle mr-1"></i>{{ it.rejectionReason }}
            </div>

            <!-- Bekleyen kalem için Onayla / Revize Et / Reddet -->
            <div v-if="it.itemStatus === 1 && item.status !== 7 && item.status !== 8" class="flex space-x-2 mt-2">
              <button class="flex-1 px-2 py-1 bg-green-500 text-white text-xxs rounded" @click="approveItem(it)">
                <i class="fas fa-check mr-1"></i>Onayla
              </button>
              <button class="flex-1 px-2 py-1 text-white text-xxs rounded" style="background-color: #2563eb;" @click="reviseItem(it)">
                <i class="fas fa-edit mr-1"></i>Revize Et
              </button>
              <button class="flex-1 px-2 py-1 bg-red-500 text-white text-xxs rounded" @click="rejectItem(it)">
                <i class="fas fa-times mr-1"></i>Reddet
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Fişten okunan değerler. OCR yanlış okuyabildiği ve bu değerler
           Siber'e giden litreyi belirlediği için operatör düzeltebiliyor. -->
      <div v-if="item.receiptFileUrl" class="border border-gray-200 rounded p-2 mb-2 bg-gray-50">
        <div class="flex items-center justify-between mb-1">
          <span class="text-xs font-bold text-gray-700">
            <i class="fas fa-receipt mr-1"></i>Fişten Okunan
          </span>
          <span v-if="item.ocrConfidence != null" :class="ocrBadgeClass" class="text-xxs px-2 py-0.5 rounded">
            {{ ocrConfidenceLabel }}
          </span>
          <span v-else class="text-xxs px-2 py-0.5 rounded bg-gray-200 text-gray-700">Okunamadı</span>
        </div>

        <div v-if="!ocrEditing" class="text-xs text-gray-700">
          <div class="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <div><span class="text-gray-500">İstasyon:</span> {{ item.ocrStationName || "—" }}</div>
            <div><span class="text-gray-500">Litre:</span> {{ item.ocrLiters != null ? item.ocrLiters + " Lt" : "—" }}</div>
            <div><span class="text-gray-500">Tutar:</span> {{ item.ocrTotalPrice != null ? item.ocrTotalPrice + " TL" : "—" }}</div>
            <div><span class="text-gray-500">Birim:</span> {{ item.ocrUnitPrice != null ? item.ocrUnitPrice + " TL/Lt" : "—" }}</div>
          </div>
          <button class="mt-2 px-2 py-1 text-white text-xxs rounded" style="background-color: #2563eb;" @click="startOcrEdit">
            <i class="fas fa-pen mr-1"></i>Düzelt
          </button>
        </div>

        <div v-else class="text-xs">
          <p class="text-gray-500 mb-1">Fişteki gerçek değerleri girin. Siber'e bu değerler gönderilir.</p>
          <div class="grid grid-cols-2 gap-2 mb-2">
            <label class="block">
              <span class="text-gray-500">İstasyon</span>
              <input v-model="ocrForm.stationName" type="text" class="w-full border rounded px-1 py-0.5" />
            </label>
            <label class="block">
              <span class="text-gray-500">Litre</span>
              <input v-model.number="ocrForm.liters" type="number" step="0.01" min="0" class="w-full border rounded px-1 py-0.5" />
            </label>
            <label class="block">
              <span class="text-gray-500">Tutar (TL)</span>
              <input v-model.number="ocrForm.totalPrice" type="number" step="0.01" min="0" class="w-full border rounded px-1 py-0.5" />
            </label>
            <label class="block">
              <span class="text-gray-500">Birim (boş = hesapla)</span>
              <input v-model.number="ocrForm.unitPrice" type="number" step="0.0001" min="0" class="w-full border rounded px-1 py-0.5" />
            </label>
          </div>
          <div class="flex gap-2">
            <button class="px-2 py-1 bg-green-600 text-white text-xxs rounded" @click="saveOcrEdit">
              <i class="fas fa-check mr-1"></i>Kaydet
            </button>
            <button class="px-2 py-1 bg-gray-400 text-white text-xxs rounded" @click="ocrEditing = false">İptal</button>
          </div>
        </div>
      </div>

      <!-- İşlemler: fiş görüntüleme, düzenleme ve iptal.
           Düzenle/İptal Et satır içindeki butonlardan buraya taşındı. -->
      <div v-if="item.receiptFileUrl || canModify" class="flex flex-wrap gap-2 pt-3 mt-3 border-t border-gray-200">
        <button v-if="item.receiptFileUrl" class="px-3 py-1 text-white text-xs rounded" style="background-color: #f97316;" @click="$emit('view-receipt', item.receiptFileUrl, item.id)">
          <i class="fas fa-receipt mr-1"></i>Fişi Gör
        </button>
        <button v-if="canModify" class="px-3 py-1 text-white text-xs rounded" style="background-color: #ca8a04;" @click="$emit('open-edit', item)">
          <i class="fas fa-edit mr-1"></i>Düzenle
        </button>
        <button v-if="canModify" class="px-3 py-1 text-white text-xs rounded" style="background-color: #dc2626;" @click="$emit('open-cancel', item)">
          <i class="fas fa-ban mr-1"></i>İptal Et
        </button>
      </div>

    </div>
  </modal>
</template>

<script>
export default {
  name: "request-detail-modal",
  props: {
    item: { type: Object, default: null },
  },
  data() {
    return {
      ocrEditing: false,
      ocrForm: { stationName: "", liters: null, totalPrice: null, unitPrice: null },
    };
  },
  computed: {
    ocrConfidenceLabel() {
      const c = Number(this.item?.ocrConfidence);
      if (!Number.isFinite(c)) return "—";
      if (c >= 100) return "Elle doğrulandı";
      return `Güven %${c}`;
    },
    ocrBadgeClass() {
      const c = Number(this.item?.ocrConfidence);
      if (c >= 100) return "bg-blue-100 text-blue-800";
      if (c >= 80) return "bg-green-100 text-green-800";
      if (c >= 50) return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    },
    // Düzenleme/iptal yalnızca onaylanmış, yönlendirilmiş veya yakıt alınmış
    // taleplerde. Number() bilinçli: status string gelirse includes() sessizce
    // false döner ve butonlar kaybolur.
    canModify() {
      return this.item ? [2, 3, 4].includes(Number(this.item.status)) : false;
    },
    canbusChange() {
      if (!this.item) return "—";
      const b = this.item.canbusFuelLevelBefore;
      const a = this.item.canbusFuelLevelAfter;
      if (b == null || a == null) return "—";
      const diff = Number(a) - Number(b);
      const sign = diff > 0 ? "+" : "";
      return sign + diff.toFixed(2) + "%";
    },
    canbusChangeClass() {
      if (!this.item) return "";
      const b = this.item.canbusFuelLevelBefore;
      const a = this.item.canbusFuelLevelAfter;
      if (b == null || a == null) return "text-gray-500";
      const diff = Number(a) - Number(b);
      return diff > 0 ? "text-green-600" : (diff < 0 ? "text-red-600" : "text-gray-500");
    },
  },
  methods: {
    startOcrEdit() {
      // Mevcut okunan değerlerle başlat; operatör sıfırdan yazmasın.
      this.ocrForm = {
        stationName: this.item.ocrStationName || "",
        liters: this.item.ocrLiters != null ? Number(this.item.ocrLiters) : null,
        totalPrice: this.item.ocrTotalPrice != null ? Number(this.item.ocrTotalPrice) : null,
        unitPrice: this.item.ocrUnitPrice != null ? Number(this.item.ocrUnitPrice) : null,
      };
      this.ocrEditing = true;
    },
    saveOcrEdit() {
      if (!this.ocrForm.liters || this.ocrForm.liters <= 0) {
        this.$emit("ocr-error", "Litre sıfırdan büyük olmalıdır");
        return;
      }
      if (!this.ocrForm.totalPrice || this.ocrForm.totalPrice <= 0) {
        this.$emit("ocr-error", "Tutar sıfırdan büyük olmalıdır");
        return;
      }
      this.$emit("correct-ocr", {
        requestId: this.item.id,
        stationName: this.ocrForm.stationName || null,
        liters: this.ocrForm.liters,
        totalPrice: this.ocrForm.totalPrice,
        unitPrice: this.ocrForm.unitPrice || null,
      });
      this.ocrEditing = false;
    },

    formatPercent(val) {
      if (val == null) return "—";
      return Number(val).toFixed(2) + "%";
    },
    itemStatusBadge(s) {
      if (s === 1) return { label: "Onay Bekliyor", cls: "bg-yellow-100 text-yellow-800" };
      if (s === 2) return { label: "Onaylandı", cls: "bg-green-100 text-green-800" };
      if (s === 7) return { label: "Reddedildi", cls: "bg-red-100 text-red-700" };
      return { label: "—", cls: "bg-gray-100 text-gray-600" };
    },
    formatMoney(val) {
      if (val == null) return "0,00";
      return Number(val).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    approvedDisplay(it) {
      // Onay bekliyor → —
      if (it.itemStatus === 1) return "—";
      // Reddedildi → —
      if (it.itemStatus === 7) return "—";
      // Onaylandı (itemStatus 2 veya daha ileri)
      if (it.approvedLiters != null) return it.approvedLiters + " Lt";
      if (it.isFillTank) return "TAM DOLUM";
      return "—";
    },
    requestedDisplay(it) {
      if (it.isFillTank) return "TAM DOLUM";
      return (it.requestedLiters || 0) + " Lt";
    },
    approveItem(it) {
      // Direkt onay: TAM DOLUM → null, normal kalem → talep edilen litre
      const approvedLiters = it.isFillTank ? null : (it.requestedLiters || 0);
      const detailText = it.isFillTank
        ? `<b>${it.fuelType}</b> kalemi <b>TAM DOLUM</b> olarak onaylansın mı?`
        : `<b>${it.fuelType}</b> kalemi <b>${it.requestedLiters} Lt</b> olarak onaylansın mı?`;
      this.$swal.fire({
        title: `${it.fuelType} — Onay`,
        html: detailText + `<br/><br/>Farklı bir değer onaylamak isterseniz <b>Revize Et</b> butonunu kullanın.`,
        showCancelButton: true,
        confirmButtonText: "Onayla",
        cancelButtonText: "Vazgeç",
      }).then((res) => {
        if (res.isConfirmed) {
          this.$emit("approve-item", { requestId: this.item.id, itemId: it.id, approvedLiters });
        }
      });
    },
    reviseItem(it) {
      const isFill = !!it.isFillTank;
      const html = isFill
        ? `Sürücü <b>TAM DOLUM</b> talep etti.<br/>Belirli bir litreye sınırlamak için aşağıya değer girin.`
        : `Sürücü <b>${it.requestedLiters} Lt</b> talep etti.<br/>Farklı bir litre onaylamak için aşağıya değer girin.`;
      this.$swal.fire({
        title: `${it.fuelType} — Revize Onay`,
        html,
        input: "number",
        inputValue: isFill ? "" : (it.requestedLiters || ""),
        inputAttributes: { placeholder: "Onaylanacak litre", min: 1 },
        showCancelButton: true,
        confirmButtonText: "Revize Onayla",
        cancelButtonText: "İptal",
        inputValidator: (val) => {
          const n = Number(val);
          if (!n || n <= 0) return "Geçerli bir litre değeri giriniz";
          return null;
        },
      }).then((res) => {
        if (res.isConfirmed) {
          this.$emit("approve-item", { requestId: this.item.id, itemId: it.id, approvedLiters: Number(res.value) });
        }
      });
    },
    rejectItem(it) {
      this.$swal.fire({
        title: "Kalem Reddi",
        text: "Red nedenini giriniz",
        input: "text",
        showCancelButton: true,
        confirmButtonText: "Reddet",
        cancelButtonText: "İptal",
      }).then((res) => {
        if (res.isConfirmed) {
          this.$emit("reject-item", { requestId: this.item.id, itemId: it.id, rejectionReason: res.value || "" });
        }
      });
    },
  },
};
</script>
